import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ILocationState } from '../../../../models';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { Avatar } from '../../../components-v2/Avatar';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { THEME } from '../../../components-v2/Theme/theme';
import { FeedCardNoteContent } from '../../cards/FeedCardNoteContent';
import { FullscreenModalNavLink } from '../../fullscreen/FullscreenModalNavLink';
import { VisibilityIndicatorIcon } from '../../VisibilityIndicatorIcon';
import { EmailMessageHeader } from '../EmailMessageHeader';

const MAX_NAMED_ENTITIES = 2;

interface IProps {
	note: Api.NoteViewModel;
	onEditNote: (id: string) => void;
	onDeleteNote: (id: string) => void;
}

function initialsOf(name: string): string {
	return name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map(part => part[0]?.toUpperCase() ?? '')
		.join('');
}

function entityPath(entity: Api.EntityViewModel): string {
	const collection = Api.getTypeForEntity(entity.toJs()) === 'company' ? 'companies' : 'people';
	return `/${collection}/${entity.id}`;
}

function _NoteRow({ note, onEditNote, onDeleteNote }: IProps) {
	const userSession = useUserSession();
	const userIsCreator = note.creatorId === userSession.user.id;
	const wasCreatedByEmail = !!note.context && note.context.source === Api.RichContentContextSource.EmailMessageSend;
	const mentioned = note.mentionedEntities
		.filter(mention => mention.method === Api.RichContentReferenceMethod.Explicit)
		.map(mention => mention.entity);
	const named = mentioned.slice(0, MAX_NAMED_ENTITIES);
	const overflow = mentioned.length - named.length;
	const primary = mentioned[0];

	const renderEmailHeader = () => {
		const recipients = (note.mentionedEntities || [])
			.filter(x => Api.getTypeForEntity(x.entity.toJs()) !== 'company')
			.map(x => x.entity as Api.ContactViewModel);
		const context = note.context as Api.IEmailMessageSendContext;
		return (
			<EmailMessageHeader
				className={css(styleSheet.emailHeader)}
				recipients={recipients}
				seenOnDate={context.openDate ? new Date(context.openDate) : undefined}
			/>
		);
	};

	return (
		<article className={css(styleSheet.row)} data-id={note.id}>
			<header className={css(styleSheet.header)}>
				{primary ? (
					<Avatar
						fallbackText={initialsOf(primary.name)}
						imgSrc={(primary.toJs() as Api.IContact).profilePic || undefined}
						smallText
						styles={[styleSheet.avatar]}
					/>
				) : null}
				<div className={css(styleSheet.titleBlock)}>
					<h3 className={css(styleSheet.title)}>
						{named.length > 0 ? 'Note about ' : 'Note'}
						{named.map((entity, index) => {
							const locationState: ILocationState<Api.EntityViewModel, Api.IEntity> = { viewModel: entity };
							return (
								<React.Fragment key={entity.id}>
									{index > 0 ? (index === named.length - 1 && overflow === 0 ? ' and ' : ', ') : null}
									<FullscreenModalNavLink
										className={css(styleSheet.entityLink)}
										title={entity.name}
										to={{ pathname: entityPath(entity), state: locationState }}
									>
										{entity.name}
									</FullscreenModalNavLink>
								</React.Fragment>
							);
						})}
						{overflow > 0 ? ` and ${overflow} other${overflow > 1 ? 's' : ''}` : null}
					</h3>
					<div className={css(styleSheet.meta)}>
						{userIsCreator && note.visibility !== 'all' ? (
							<VisibilityIndicatorIcon className={css(styleSheet.visibility)} visibility={note.visibility} />
						) : null}
						<span>{userIsCreator ? 'You' : note.creatorName}</span>
						<span aria-hidden>·</span>
						<span>{note.lastModifiedDateText}</span>
					</div>
				</div>
				{note.canEdit || note.canDelete ? (
					<Dropdown>
						<DropdownEllipsisTrigger aria-label='Note actions' />
						<DropdownContent align='end'>
							{note.canEdit ? <DropdownItem onClick={() => onEditNote(note.id)}>Edit</DropdownItem> : null}
							{note.canDelete ? (
								<DropdownItem styles={[styleSheet.deleteItem]} onClick={() => onDeleteNote(note.id)}>
									Delete
								</DropdownItem>
							) : null}
						</DropdownContent>
					</Dropdown>
				) : null}
			</header>
			{wasCreatedByEmail ? renderEmailHeader() : null}
			<FeedCardNoteContent note={note} styles={[styleSheet.content]} />
		</article>
	);
}

export const NoteRow = observer(_NoteRow);

const styleSheet = StyleSheet.create({
	row: {
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		padding: THEME.spacing.md,
		width: '100%',
	},
	header: {
		alignItems: 'center',
		display: 'flex',
		gap: THEME.spacing.sm,
		minWidth: 0,
	},
	avatar: {
		flexShrink: 0,
		height: 36,
		width: 36,
	},
	titleBlock: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: 2,
		minWidth: 0,
	},
	title: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
		margin: 0,
		...THEME.common.truncate,
	},
	entityLink: {
		color: THEME.colors.primary.main,
		textDecoration: 'none',
		':hover': {
			textDecoration: 'underline',
		},
	},
	meta: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		fontSize: THEME.typography.fontSizes.xs,
		gap: THEME.spacing.xs,
	},
	visibility: {
		flexShrink: 0,
	},
	emailHeader: {
		marginTop: 0,
	},
	content: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		marginTop: 0,
	},
	deleteItem: {
		color: THEME.colors.danger.dark,
	},
});
